import { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ProLayout, PageContainer } from '@ant-design/pro-layout';
import { Alert, App, Avatar, Button, Flex, Space, Tag, Tooltip } from 'antd';
import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BellOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  FileProtectOutlined,
  GiftOutlined,
  GlobalOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SoundOutlined,
  SwapOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/stores/authStore';
import { staffAuthService } from '@/services/staffAuth';
import { getActiveNavigationPath, getNavigation, getPageName } from '@/navigation';

const icons = {
  '/dashboard': <DashboardOutlined />,
  '/users': <TeamOutlined />,
  '/kyc-manual-review': <AuditOutlined />,
  '/residence-reviews': <HomeOutlined />,
  '/tradevest-onboarding': <SafetyCertificateOutlined />,
  '/transaction-queue': <SwapOutlined />,
  '/subscriptions': <CreditCardOutlined />,
  '/card-renewals': <SyncOutlined />,
  '/three-ds': <SafetyCertificateOutlined />,
  '/finapi-oversight': <BankOutlined />,
  '/assets': <AppstoreOutlined />,
  '/issuers': <GlobalOutlined />,
  '/broadcasts': <SoundOutlined />,
  '/notifications': <BellOutlined />,
  '/referrals': <GiftOutlined />,
  '/staff': <UserOutlined />,
  '/app-settings': <SettingOutlined />,
  '/legal-documents': <FileProtectOutlined />,
};

export function BackofficeLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const staff = useAuthStore(state => state.staff);
  const logoutStore = useAuthStore(state => state.logout);
  const { message } = App.useApp();
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches,
  );
  const [loggingOut, setLoggingOut] = useState(false);
  const activePath = getActiveNavigationPath(location.pathname);
  const pageName = getPageName(location.pathname);
  const isStaging = import.meta.env.VITE_ENV === 'staging';
  const isPreview = import.meta.env.VITE_ENV === 'production-preview';
  const groups = getNavigation(staff?.role);

  const logout = async () => {
    setLoggingOut(true);
    try {
      await staffAuthService.logout();
    } catch {
      // Always clear the local session; the store logout also clears cached admin data (see queryClient.ts).
    } finally {
      logoutStore();
      navigate('/login', { replace: true });
      message.success('Abgemeldet');
    }
  };

  return (
    <ProLayout
      className={`backoffice-shell${isStaging ? ' backoffice-shell--staging' : ''}`}
      title="mygate"
      logo={false}
      layout="side"
      locale="en-US"
      siderWidth={256}
      fixSiderbar
      fixedHeader={false}
      collapsed={collapsed}
      onCollapse={setCollapsed}
      collapsedButtonRender={() => (
        <Button
          className="backoffice-collapse-button"
          size="small"
          shape="circle"
          aria-label={collapsed ? 'Navigation ausklappen' : 'Navigation einklappen'}
          aria-expanded={!collapsed}
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={() => setCollapsed(value => !value)}
        />
      )}
      headerRender={(props, defaultDom) =>
        props.isMobile ? (
          <Flex className="backoffice-mobile-header" align="center" gap={12}>
            <Button
              type="text"
              icon={<MenuOutlined />}
              aria-label="Navigation öffnen"
              aria-expanded={!collapsed}
              onClick={() => setCollapsed(false)}
            />
            <Link to="/dashboard" className="backoffice-brand" aria-label="mygate Dashboard">
              <img src="/mygate-logo.svg" alt="mygate" />
              <span>Backoffice</span>
            </Link>
            <Tag bordered={false} color={isStaging ? 'orange' : 'cyan'}>
              {isPreview ? 'Vorschau' : isStaging ? 'Staging' : import.meta.env.DEV ? 'Lokal' : 'mygate'}
            </Tag>
          </Flex>
        ) : (
          defaultDom
        )
      }
      contentStyle={{ padding: 0, minWidth: 0 }}
      siderMenuType="group"
      stylish={{
        sider: () => ({
          '.ant-pro-sider-logo, .ant-pro-sider-actions, .ant-pro-sider-footer': { marginInline: 8 },
          '.ant-menu-item-group-title': { color: '#a7bfbc', paddingBlock: 6 },
          '.ant-menu-item-divider': { marginBlock: 6 },
        }),
      }}
      menu={{ locale: false }}
      menuProps={{ selectedKeys: [activePath], style: { paddingInline: 8 } }}
      location={location}
      route={{
        children: groups.map(group => ({
          key: group.name,
          name: group.name,
          children: group.items.map(item => ({
            key: item.path,
            path: item.path,
            name: item.name,
            icon: icons[item.path as keyof typeof icons],
          })),
        })),
      }}
      menuItemRender={(item, dom, props) => (
        <Link
          to={item.path ?? '/dashboard'}
          onClick={() => {
            if (props.isMobile) setCollapsed(true);
          }}
        >
          {dom}
        </Link>
      )}
      menuHeaderRender={(_logo, _title, props) => (
        <Link
          to="/dashboard"
          className="backoffice-brand"
          aria-label="mygate Dashboard"
          onClick={() => {
            if (props?.isMobile) setCollapsed(true);
          }}
        >
          <img src="/mygate-logo.svg" alt="mygate" />
          {(!props?.collapsed || props?.isMobile) && <span>Backoffice</span>}
        </Link>
      )}
      headerContentRender={false}
      actionsRender={props =>
        props.collapsed && !props.isMobile
          ? []
          : [
              <Tag
                key="environment"
                bordered={false}
                color={isStaging ? 'orange' : import.meta.env.DEV ? 'cyan' : 'default'}
              >
                {isPreview
                  ? 'Live-Vorschau · Nur Lesen'
                  : isStaging
                    ? 'Staging'
                    : import.meta.env.DEV
                      ? props.isMobile
                        ? 'Lokal'
                        : 'Lokale Entwicklung'
                      : 'Backoffice'}
              </Tag>,
            ]
      }
      pageTitleRender={() => `${pageName} · ${import.meta.env.VITE_APP_NAME || 'mygate Backoffice'}`}
      menuFooterRender={props => (
        <div className="backoffice-account">
          {!props?.collapsed && (
            <Space align="start" style={{ marginBottom: 12 }}>
              <Avatar icon={<UserOutlined />} style={{ background: '#235459' }} />
              <div>
                <div className="backoffice-account-name">
                  {staff?.firstName} {staff?.lastName}
                </div>
                <small>{staff?.role === 'ADMIN' ? 'Administrator' : 'Support'}</small>
              </div>
            </Space>
          )}
          <Tooltip title={props?.collapsed ? 'Abmelden' : undefined}>
            <Button
              className="backoffice-logout"
              type="text"
              block={!props?.collapsed}
              icon={<LogoutOutlined />}
              aria-label="Abmelden"
              loading={loggingOut}
              onClick={() => void logout()}
            >
              {!props?.collapsed && 'Abmelden'}
            </Button>
          </Tooltip>
        </div>
      )}
      token={{
        bgLayout: '#f2f1ec',
        sider: {
          // Inset the menu content, not its scroll container, so the scrollbar stays at the outer edge.
          paddingInlineLayoutMenu: 0,
          colorMenuBackground: '#102f33',
          colorTextMenu: '#d3e0dc',
          colorTextMenuSecondary: '#9eb7b5',
          colorTextMenuTitle: '#ffffff',
          colorTextMenuSelected: '#ffffff',
          colorTextMenuItemHover: '#ffffff',
          colorBgMenuItemSelected: '#285256',
          colorBgMenuItemHover: '#1c4347',
          colorTextSubMenuSelected: '#ffffff',
          colorMenuItemDivider: '#2b4c4f',
          colorBgMenuItemCollapsedElevated: '#102f33',
        },
        header: { colorBgHeader: '#ffffff', colorHeaderTitle: '#203638', heightLayoutHeader: 64 },
        pageContainer: { paddingInlinePageContainerContent: 32, paddingBlockPageContainerContent: 24 },
      }}
    >
      <PageContainer
        title={<h1 className="backoffice-page-title">{pageName}</h1>}
        breadcrumbRender={false}
        header={{ style: { background: '#fff' } }}
      >
        {isPreview && (
          <Alert
            type="info"
            showIcon
            message="Live-Produktivdaten · Nur Lesen"
            description="Direkt mit der Produktions-API verbunden. Änderungen und Versand sind in dieser Vorschau gesperrt."
            style={{ marginBottom: 24 }}
          />
        )}
        <Outlet />
      </PageContainer>
    </ProLayout>
  );
}
